// Copyright (c) Microsoft. All rights reserved.

import { IChatRoom, IChatRoomList, IChatStatus } from '../models/conversations/ChatRoom';
import { IChatMessage, IChatMessageList } from '../models/conversations/ChatMessage';
import { IChatParticipant } from '../models/conversations/ChatParticipant';
import { IChatSession } from '../models/conversations/ChatSession';
import { IChatUser } from '../models/users/ChatUser';
import { ServiceInfo } from '../models/ServiceInfo';
import { IAsk } from '../semantic-kernel/model/Ask';
import { IAskResult } from '../semantic-kernel/model/AskResult';
import { BaseService } from './BaseService';
import { AIModelAPI } from '../models/app/AIModel';
import { IChatKeyword } from '../models/conversations/ChatKeyword';
import { IFileAnalyzedCacheResult } from '../models/conversations/File';

export class ChatService extends BaseService {
  public createChatAsync = async (): Promise<IChatRoom> => {
    const result = await this.getResponseAsync<IChatRoom>(
      {
        commandPath: 'api/chatroom',
        method: 'POST',
      },
      // accessToken,
    );

    return result;
  };

  public getChatAsync = async (chatId: string): Promise<IChatSession> => {
    const result = await this.getResponseAsync<IChatSession>({
      commandPath: `chats/${chatId}`,
      method: 'GET',
    });

    return result;
  };

  public getAllChatsAsync = async (continuationToken: string | null): Promise<IChatRoomList> => {
    const params = new URLSearchParams();
    params.append('count', process.env.REACT_APP_CHAT_LIST_COUNT as string);

    if (continuationToken !== null) {
      params.append('continuationToken', continuationToken);
    }

    const result = await this.getResponseAsync<IChatRoomList>(
      {
        commandPath: 'api/chatroom/list',
        query: params,
        method: 'GET',
      },
      // accessToken,
    );
    return result;
  };

  public getChatStatus = async (): Promise<IChatStatus[]> => {
    const result = await this.getResponseAsync<IChatStatus[]>(
      {
        commandPath: 'api/v2/chat/status',
        method: 'GET',
      },
      // accessToken,
    );
    return result;
  }

  public getAllChatsByChatRoomIdAsync = async (chatRoomId: string | undefined): Promise<IChatRoomList> => {
    const params = new URLSearchParams();
    params.append('count', process.env.REACT_APP_CHAT_LIST_COUNT as string);

    const result = await this.getResponseAsync<IChatRoomList>(
      {
        commandPath: `api/chatroom/list/${chatRoomId}`,
        query: params,
        method: 'GET',
      },
      // accessToken,
    );
    return result;
  };

  public getAllChatMessagesAsync = async (chatId: string): Promise<IChatMessage[]> => {
    const result = await this.getResponseAsync<IChatMessageList>({
      commandPath: `api/chat/list?chatRoomId=${chatId}`,
    });

    return result.data;
  };

  public getChatMessagesAsync = async (
    chatRoomId: string,
    continuationToken: string | null,
  ): Promise<IChatMessageList> => {
    const query = new URLSearchParams();
    query.append('chatRoomId', chatRoomId);
    query.append('count', process.env.REACT_APP_CHAT_LIST_COUNT as string);
    if (continuationToken !== null) {
      query.append('continuationToken', continuationToken);
    }

    const result = await this.getResponseAsync<IChatMessageList>({
      commandPath: `api/chat/list`,
      method: 'GET',
      query: query,
    });

    // Messages are returned with most recent message at index 0 and oldest message at the last index,
    // so we need to reverse the order for render
    return result;
  };

  public editChatAsync = async (chatRoom: IChatRoom): Promise<any> => {
    const body = { ...chatRoom };
    const result = await this.getResponseAsync<IChatRoom>({
      commandPath: `api/chatroom`,
      method: 'PUT',
      body,
    });

    return result;
  };

  public deleteChatAsync = async (chatRoom: IChatRoom): Promise<object> => {
    const body = { ...chatRoom };
    const result = await this.getResponseAsync<object>({
      commandPath: `api/chatroom`,
      method: 'DELETE',
      body,
    });

    return result;
  };

  public getBotResponseAsync = async (ask: IAsk, processPlan = false): Promise<IAskResult> => {
    // If function requires any additional api properties, append to context
    const chatId = ask.variables?.find((variable) => variable.key === 'chatId')?.value as string;

    const result = await this.getResponseAsync<IAskResult>({
      commandPath: `chats/${chatId}/${processPlan ? 'plan' : 'messages'}`,
      method: 'POST',
      body: ask,
    });

    return result;
  };

  public joinChatAsync = async (chatId: string): Promise<IChatSession> => {
    await this.getResponseAsync<any>({
      commandPath: `chats/${chatId}/participants`,
      method: 'POST',
    });

    return await this.getChatAsync(chatId);
  };

  public getAllChatParticipantsAsync = async (chatId: string): Promise<IChatUser[]> => {
    const result = await this.getResponseAsync<IChatParticipant[]>({
      commandPath: `chats/${chatId}/participants`,
      method: 'GET',
    });

    const chatUsers = result.map<IChatUser>((participant) => ({
      id: participant.userId,
      online: false,
      fullName: '', // The user's full name is not returned from the server
      emailAddress: '', // The user's email address is not returned from the server
      isTyping: false,
      photo: '',
    }));

    return chatUsers;
  };

  public getSemanticMemoriesAsync = async (chatId: string, memoryName: string): Promise<string[]> => {
    const result = await this.getResponseAsync<string[]>({
      commandPath: `chats/${chatId}/memories?type=${memoryName}`,
      method: 'GET',
    });

    return result;
  };

  public getServiceInfoAsync = async (): Promise<ServiceInfo> => {
    const result = await this.getResponseAsync<ServiceInfo>({
      commandPath: `info`,
      method: 'GET',
    });

    return result;
  };

  public getAllModelAsync = async (): Promise<AIModelAPI[]> => {
    const result = await this.getResponseAsync<AIModelAPI[]>({
      commandPath: `api/user/model`,
    });

    return result;
  };

  public getChatKeywords = async (): Promise<IChatKeyword[]> => {
    const result = await this.getResponseAsync<IChatKeyword[]>({
      commandPath: `api/user/keyword`,
    });
    return result;
  };

  public getFileAnaylzedCacheResult = async (file: File): Promise<IFileAnalyzedCacheResult> => {
    const formData = new FormData();
    formData.append('file', file);

    // const body = {...file}
    const result = await this.getResponseAsync<IFileAnalyzedCacheResult>({
      commandPath: `api/file/analyze`,
      method: 'POST',
      body: formData,
    });
    return result;
    // try {
    //   const result = await this.getResponseAsync<IFileAnalyzedCacheResult>({
    //     commandPath: `api/file/analyze`,
    //     method: 'POST',
    //     body: formData,
    //   });
    //   return result;
    // } catch (error) {
    //   console.log(error);
    //   console.log(typeof error);
    //   console.log(error.response);
    //   console.log(error.data);
    // }
  };

  public createChatMessage = (chatMessage: IChatMessage): Promise<IChatMessage> => {
    const result = this.getResponseAsync<IChatMessage>(
      {
        commandPath: 'api/chat',
        method: 'POST',
        body: chatMessage,
      },
      // accessToken,
    );
    return result;
  };
}
